// src/utils/customerGroupModel.js

// 📋 Customer Group Schema
export const CustomerGroupSchema = {
  _id: '',
  name: '',
  description: '',
  customers: [],
  isActive: true,
  createdBy: null,
  createdAt: null,
  updatedAt: null
}

// ✅ Validation Rules
export const VALIDATION_RULES = {
  name: {
    required: true,
    minLength: 2,
    maxLength: 50,
    pattern: /^[a-zA-Z0-9\s\-&'().]+$/,
    message: 'Name must be 2-50 characters and contain only letters, numbers, spaces, and common symbols'
  },
  description: {
    required: false,
    maxLength: 200,
    message: 'Description cannot exceed 200 characters'
  }
}

// 🔄 Customer Group Model Utilities
export class CustomerGroupModel {
  
  // Create a new customer group instance
  static create(data = {}) {
    return {
      ...CustomerGroupSchema,
      ...data,
      _id: data._id || '',
      isActive: data.isActive !== false,
      customers: Array.isArray(data.customers) ? data.customers : []
    }
  }

  // Validate customer group data
  static validate(groupData) {
    const errors = {}
    
    // Validate name
    if (!groupData.name || !groupData.name.trim()) {
      errors.name = 'Group name is required'
    } else if (groupData.name.trim().length < VALIDATION_RULES.name.minLength) {
      errors.name = `Name must be at least ${VALIDATION_RULES.name.minLength} characters`
    } else if (groupData.name.trim().length > VALIDATION_RULES.name.maxLength) {
      errors.name = `Name cannot exceed ${VALIDATION_RULES.name.maxLength} characters`
    } else if (!VALIDATION_RULES.name.pattern.test(groupData.name.trim())) {
      errors.name = VALIDATION_RULES.name.message
    }
    
    // Validate description
    if (groupData.description && groupData.description.length > VALIDATION_RULES.description.maxLength) {
      errors.description = VALIDATION_RULES.description.message
    }
    
    return {
      isValid: Object.keys(errors).length === 0,
      errors
    }
  }

  // Validate partial data (for updates)
  static validatePartial(groupData) {
    const errors = {}
    
    if (groupData.name !== undefined) {
      if (!groupData.name.trim()) {
        errors.name = 'Group name cannot be empty'
      } else if (groupData.name.trim().length < VALIDATION_RULES.name.minLength) {
        errors.name = `Name must be at least ${VALIDATION_RULES.name.minLength} characters`
      } else if (groupData.name.trim().length > VALIDATION_RULES.name.maxLength) {
        errors.name = `Name cannot exceed ${VALIDATION_RULES.name.maxLength} characters`
      }
    }
    
    if (groupData.description !== undefined && groupData.description.length > VALIDATION_RULES.description.maxLength) {
      errors.description = VALIDATION_RULES.description.message
    }
    
    return {
      isValid: Object.keys(errors).length === 0,
      errors
    }
  }

  // Format customer group for display
  static formatForDisplay(group) {
    if (!group) return null
    
    return {
      ...group,
      formattedName: this.formatName(group),
      customerCount: this.getCustomerCount(group),
      formattedCustomerNames: this.formatCustomerNames(group),
      formattedCreatedAt: this.formatDate(group.createdAt),
      formattedUpdatedAt: this.formatDate(group.updatedAt),
      statusBadgeClasses: this.getStatusBadgeClasses(group.isActive),
      customerCountBadge: this.getCustomerCountBadge(group.customers)
    }
  }

  // Format name with fallback
  static formatName(group) {
    return group?.name || 'Unnamed Group'
  }

  // Get customer count
  static getCustomerCount(group) {
    if (!group || !Array.isArray(group.customers)) return 0
    return group.customers.length
  }

  // Format customer names for display
  static formatCustomerNames(group) {
    if (!group || !Array.isArray(group.customers) || group.customers.length === 0) {
      return 'No customers'
    }
    
    const customerNames = group.customers
      .map(customer => {
        if (typeof customer === 'object' && customer.name) {
          return customer.name
        } else if (typeof customer === 'string') {
          return customer
        }
        return 'Unknown Customer'
      })
      .slice(0, 3) // Show only first 3 names
    
    if (group.customers.length > 3) {
      return `${customerNames.join(', ')} + ${group.customers.length - 3} more`
    }
    
    return customerNames.join(', ')
  }

  // Format date
  static formatDate(dateString) {
    if (!dateString) return 'N/A'
    
    try {
      const date = new Date(dateString)
      if (isNaN(date.getTime())) return 'Invalid Date'
      
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    } catch (error) {
      return 'Invalid Date'
    }
  }

  // Get status badge classes
  static getStatusBadgeClasses(isActive) {
    return isActive 
      ? 'bg-green-100 text-green-800 border-green-200'
      : 'bg-gray-100 text-gray-800 border-gray-200'
  }

  // Get customer count badge
  static getCustomerCountBadge(customers) {
    const count = Array.isArray(customers) ? customers.length : 0
    
    if (count === 0) {
      return 'bg-gray-100 text-gray-800'
    } else if (count < 10) {
      return 'bg-blue-100 text-blue-800'
    } else if (count < 50) {
      return 'bg-green-100 text-green-800'
    } else {
      return 'bg-purple-100 text-purple-800'
    }
  }

  // Transform customer group data for API
  static transformForAPI(groupData) {
    return {
      name: groupData.name?.trim(),
      description: groupData.description?.trim() || '',
      customers: this.extractCustomerIds(groupData.customers),
      isActive: groupData.isActive !== false
    }
  }

  // Extract customer IDs from customers array
  static extractCustomerIds(customers) {
    if (!Array.isArray(customers)) return []
    
    return customers
      .map(customer => {
        if (typeof customer === 'object' && customer._id) {
          return customer._id
        } else if (typeof customer === 'string') {
          return customer
        }
        return null
      })
      .filter(Boolean)
  }

  // Search customer groups
  static searchGroups(groups, searchTerm) {
    if (!searchTerm || !Array.isArray(groups)) return groups
    
    const lowercaseSearch = searchTerm.toLowerCase()
    return groups.filter(group => {
      const name = group.name?.toLowerCase() || ''
      const description = group.description?.toLowerCase() || ''
      
      return name.includes(lowercaseSearch) || 
             description.includes(lowercaseSearch)
    })
  }

  // Filter groups by status
  static filterByStatus(groups, isActive) {
    if (typeof isActive !== 'boolean' || !Array.isArray(groups)) return groups
    return groups.filter(group => group.isActive === isActive)
  }

  // Filter groups by customer count range
  static filterByCustomerCount(groups, minCount, maxCount) {
    if (!Array.isArray(groups)) return groups
    
    return groups.filter(group => {
      const count = this.getCustomerCount(group)
      let matches = true
      
      if (typeof minCount === 'number') {
        matches = matches && count >= minCount
      }
      
      if (typeof maxCount === 'number') {
        matches = matches && count <= maxCount
      }
      
      return matches
    })
  }

  // Sort customer groups
  static sortGroups(groups, sortBy = 'name', sortOrder = 'asc') {
    if (!Array.isArray(groups)) return []
    
    return [...groups].sort((a, b) => {
      let aValue, bValue
      
      switch (sortBy) {
        case 'name':
          aValue = a.name?.toLowerCase() || ''
          bValue = b.name?.toLowerCase() || ''
          break
        case 'customerCount':
          aValue = this.getCustomerCount(a)
          bValue = this.getCustomerCount(b)
          break
        case 'createdAt':
          aValue = new Date(a.createdAt || 0)
          bValue = new Date(b.createdAt || 0)
          break
        case 'updatedAt':
          aValue = new Date(a.updatedAt || 0)
          bValue = new Date(b.updatedAt || 0)
          break
        default:
          aValue = a[sortBy] || ''
          bValue = b[sortBy] || ''
      }
      
      if (sortOrder === 'desc') {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0
      } else {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0
      }
    })
  }

  // Calculate group statistics
  static calculateGroupStats(groups) {
    if (!Array.isArray(groups)) {
      return {
        total: 0,
        active: 0,
        inactive: 0,
        totalCustomers: 0,
        averageCustomersPerGroup: 0,
        largestGroup: 0,
        emptyGroups: 0
      }
    }
    
    const stats = {
      total: groups.length,
      active: 0,
      inactive: 0,
      totalCustomers: 0,
      averageCustomersPerGroup: 0,
      largestGroup: 0,
      emptyGroups: 0
    }
    
    let totalCustomersAcrossGroups = 0
    
    groups.forEach(group => {
      // Status counts
      if (group.isActive) {
        stats.active++
      } else {
        stats.inactive++
      }
      
      // Customer counts
      const customerCount = this.getCustomerCount(group)
      totalCustomersAcrossGroups += customerCount
      
      if (customerCount === 0) {
        stats.emptyGroups++
      }
      
      if (customerCount > stats.largestGroup) {
        stats.largestGroup = customerCount
      }
    })
    
    // Note: This counts customers multiple times if they're in multiple groups
    stats.totalCustomers = totalCustomersAcrossGroups
    stats.averageCustomersPerGroup = groups.length > 0 
      ? Math.round(totalCustomersAcrossGroups / groups.length * 10) / 10 
      : 0
    
    return stats
  }

  // Check if customer is in group
  static isCustomerInGroup(group, customerId) {
    if (!group || !Array.isArray(group.customers) || !customerId) {
      return false
    }
    
    return group.customers.some(customer => {
      if (typeof customer === 'object' && customer._id) {
        return customer._id === customerId
      } else if (typeof customer === 'string') {
        return customer === customerId
      }
      return false
    })
  }

  // Add customer to group
  static addCustomerToGroup(group, customer) {
    if (!group || !customer) return group
    
    const updatedGroup = { ...group }
    updatedGroup.customers = [...(group.customers || [])]
    
    const customerId = typeof customer === 'object' ? customer._id : customer
    
    // Check if customer is already in group
    if (!this.isCustomerInGroup(updatedGroup, customerId)) {
      updatedGroup.customers.push(customer)
    }
    
    return updatedGroup
  }

  // Remove customer from group
  static removeCustomerFromGroup(group, customerId) {
    if (!group || !customerId || !Array.isArray(group.customers)) {
      return group
    }
    
    const updatedGroup = { ...group }
    updatedGroup.customers = group.customers.filter(customer => {
      if (typeof customer === 'object' && customer._id) {
        return customer._id !== customerId
      } else if (typeof customer === 'string') {
        return customer !== customerId
      }
      return true
    })
    
    return updatedGroup
  }

  // Get groups that contain a specific customer
  static getGroupsForCustomer(groups, customerId) {
    if (!Array.isArray(groups) || !customerId) return []
    
    return groups.filter(group => this.isCustomerInGroup(group, customerId))
  }

  // Get all unique customers across groups
  static getAllUniqueCustomers(groups) {
    if (!Array.isArray(groups)) return []
    
    const customerMap = new Map()
    
    groups.forEach(group => {
      if (Array.isArray(group.customers)) {
        group.customers.forEach(customer => {
          const id = typeof customer === 'object' ? customer._id : customer
          if (id && !customerMap.has(id)) {
            customerMap.set(id, customer)
          }
        })
      }
    })
    
    return Array.from(customerMap.values())
  }

  // Validate customer data
  static validateCustomer(customer) {
    if (!customer) return false
    
    if (typeof customer === 'string') {
      return customer.length > 0
    }
    
    if (typeof customer === 'object') {
      return customer._id && customer._id.length > 0
    }
    
    return false
  }

  // Get group size category
  static getGroupSizeCategory(group) {
    const count = this.getCustomerCount(group)
    
    if (count === 0) return 'empty'
    if (count <= 10) return 'small'
    if (count <= 50) return 'medium'
    if (count <= 100) return 'large'
    return 'extra-large'
  }

  // Get group size label
  static getGroupSizeLabel(group) {
    const category = this.getGroupSizeCategory(group)
    const count = this.getCustomerCount(group)
    
    const labels = {
      'empty': 'Empty',
      'small': 'Small',
      'medium': 'Medium', 
      'large': 'Large',
      'extra-large': 'Extra Large'
    }
    
    return `${labels[category]} (${count})`
  }

  // Export group data for external use
  static exportGroupData(group) {
    if (!group) return null
    
    return {
      id: group._id,
      name: group.name,
      description: group.description,
      customerCount: this.getCustomerCount(group),
      customers: group.customers?.map(customer => ({
        id: typeof customer === 'object' ? customer._id : customer,
        name: typeof customer === 'object' ? customer.name : 'Unknown',
        email: typeof customer === 'object' ? customer.email : null
      })) || [],
      isActive: group.isActive,
      createdAt: group.createdAt,
      updatedAt: group.updatedAt
    }
  }

  // Import group data from external source
  static importGroupData(externalData) {
    return this.create({
      name: externalData.name || '',
      description: externalData.description || '',
      customers: externalData.customers || [],
      isActive: externalData.isActive !== false
    })
  }
}

export default CustomerGroupModel